"use client";

import { useEffect, useState, useCallback } from "react";
import { Badge, Button } from "@boostdev/design-system-components/client";
import { useClientTranslation } from "@/lib/i18n/client";
import { readCart } from "@/lib/shop/cart/actions";
import type { ResolvedCart } from "@/lib/shop/cart/resolve";
import { ShoppingCartIcon } from "@/components/atoms/icons/ShoppingCartIcon";
import { CartDrawer } from "./CartDrawer";

// ── Empty-cart sentinel ────────────────────────────────────────────────────────

const EMPTY_CART: ResolvedCart = {
  lines: [],
  droppedVariantIds: [],
  subtotalCents: 0,
  hasPhysical: false,
};

// ── Props ─────────────────────────────────────────────────────────────────────

export interface OpenCartProps {
  locale: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function OpenCart({ locale }: OpenCartProps): React.ReactElement {
  const { t } = useClientTranslation(locale, "shop");
  const [isOpen, setIsOpen] = useState(false);
  const [cart, setCart] = useState<ResolvedCart>(EMPTY_CART);

  const fetchCart = useCallback(async (): Promise<void> => {
    try {
      const resolved = await readCart();
      setCart(resolved);
    } catch {
      // silently keep last known state; errors surface via CartDrawer mutations
    }
  }, []);

  useEffect(() => {
    void fetchCart();
  }, [fetchCart]);

  useEffect(() => {
    const onChange = (): void => { void fetchCart(); };
    window.addEventListener("cart:changed", onChange);
    return () => window.removeEventListener("cart:changed", onChange);
  }, [fetchCart]);

  const totalQty = cart.lines.reduce((sum, l) => sum + l.qty, 0);

  return (
    <>
      <div style={{ position: "relative", display: "inline-flex" }}>
        <Button
          type="button"
          variant="outline"
          isIconOnly
          aria-label={t("cart.title")}
          onClick={() => setIsOpen(true)}
        >
          <ShoppingCartIcon aria-hidden />
        </Button>
        {totalQty > 0 && (
          <Badge
            variant="primary"
            style={{
              position: "absolute",
              insetBlockStart: "calc(-1 * var(--bds-space_2xs))",
              insetInlineEnd: "calc(-1 * var(--bds-space_2xs))",
              minWidth: "1.25rem",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            {totalQty}
          </Badge>
        )}
      </div>

      <CartDrawer
        locale={locale}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        cart={cart}
        onCartChange={fetchCart}
      />
    </>
  );
}
